import { useSession } from "next-auth/react";
import { useCallback, useEffect, useRef } from "react";
import { Resource } from "@/config/resources";
import { Action } from "@/types";
import { ResourcePermission } from "@/types/core/permissions";
import Link from "next/link";
import React from "react";

// Import the new permission gate components
import {
  PermissionGate,
  FieldPermissionGate,
} from "@/components/ui/permission-gate";
// Import Pusher client
import { pusherClient } from "@/lib/pusher";

// Define types for Pusher events
type PermissionUpdatedEvent = {
  userId: string;
  resourceName: Resource;
  permissions: {
    create?: boolean;
    read?: boolean;
    update?: boolean;
    delete?: boolean;
    fields?: Record<string, { read: boolean; write: boolean }>;
  };
};

type PermissionDeletedEvent = {
  userId: string;
};

type JobChangedEvent = {
  userId: string;
  jobTitle: string;
  jobId?: string | null;
  timestamp?: string;
  type?: "assignment" | "update" | "removal";
};

export type UsePermissionsReturn = {
  checkPermission: (resource: Resource, action: Action) => boolean;
  checkFieldPermission: (
    resource: Resource,
    fieldName: string,
    action: "read" | "write"
  ) => boolean;
  batchCheck: (
    checks: Array<{ resource: Resource; action: Action }>
  ) => boolean[];
  refreshPermissions: () => Promise<void>;
};

export function usePermissions(): UsePermissionsReturn {
  const { data: session, update: refreshSession } = useSession();
  const user = session?.user;

  // Force re-render when permissions change
  const [, forceUpdate] = React.useState({});

  // Set up Pusher client subscription to listen for permission changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = pusherClient.subscribe("permissions");

    // Listen for permission updates for the current user
    channel.bind(
      "permissions-updated",
      async (data: PermissionUpdatedEvent) => {
        // Only refresh if the update was for the current user
        if (data.userId === user.id) {
          try {
            await refreshSession();
            // Force a re-render
            forceUpdate({});
          } catch {
            // Handle error silently
          }
        }
      }
    );

    // Listen for permission deletions for the current user
    channel.bind(
      "permissions-deleted",
      async (data: PermissionDeletedEvent) => {
        // Only refresh if the deletion was for the current user
        if (data.userId === user.id) {
          try {
            await refreshSession();
            // Force a re-render
            forceUpdate({});
          } catch {
            // Handle error silently
          }
        }
      }
    );

    return () => {
      pusherClient.unsubscribe("permissions");
    };
  }, [user?.id, refreshSession]);

  const checkPermission = useCallback(
    (resource: Resource, action: Action): boolean => {
      if (!user) return false;

      // Admin override
      if (user.role === "admin") return true;

      const resourcePermissions = user.permissions?.[resource] as
        | ResourcePermission
        | undefined;
      if (!resourcePermissions) return false;

      switch (action) {
        case "create":
          return resourcePermissions.create;
        case "read":
          return resourcePermissions.read;
        case "update":
          return resourcePermissions.update;
        case "delete":
          return resourcePermissions.delete;
        default:
          return false;
      }
    },
    [user]
  );

  const checkFieldPermission = useCallback(
    (
      resource: Resource,
      fieldName: string,
      action: "read" | "write"
    ): boolean => {
      if (!user) return false;

      // Admin override
      if (user.role === "admin") return true;

      const resourcePermissions = user.permissions?.[resource] as
        | ResourcePermission
        | undefined;
      return resourcePermissions?.fields?.[fieldName]?.[action] ?? false;
    },
    [user]
  );

  const batchCheck = useCallback(
    (checks: Array<{ resource: Resource; action: Action }>): boolean[] => {
      return checks.map(({ resource, action }) =>
        checkPermission(resource, action)
      );
    },
    [checkPermission]
  );

  const refreshPermissions = useCallback(async (): Promise<void> => {
    try {
      await refreshSession();
    } catch {
      // Handle error silently
    }
  }, [refreshSession]);

  return {
    checkPermission,
    checkFieldPermission,
    batchCheck,
    refreshPermissions,
  };
}

// Use the new PermissionGate component
export const RequirePermission = PermissionGate;

// Use the new FieldPermissionGate component
export const RequireFieldPermission = FieldPermissionGate;

export function useRefreshSession(): { refreshSession: () => Promise<void> } {
  const { update } = useSession();

  const refreshSession = async () => {
    await update();
  };

  return { refreshSession };
}

// Add a hook for job title changes
export function useJobTitleRefresh(): {
  refreshSession: () => Promise<void>;
  jobTitle: string | null | undefined;
  isRefreshing: boolean;
} {
  const { data: session, update: refreshSession } = useSession();
  const jobTitle = session?.user?.jobTitle;
  const userId = session?.user?.id;

  // State to temporarily store expected job title from pusher event
  const [expectedJobTitle, setExpectedJobTitle] = React.useState<string | null>(
    null
  );
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const spinnerTimeoutRef = useRef<NodeJS.Timeout | null>(null); // New ref for spinner timeout
  const maxAttemptsRef = useRef(6);

  // Load persisted expected job title on component mount
  useEffect(() => {
    if (userId) {
      try {
        const storedData = localStorage.getItem(`job_title_${userId}`);
        if (storedData) {
          const parsedData = JSON.parse(storedData);

          // Only use stored data if it's relatively fresh (less than 5 minutes old)
          const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
          if (parsedData.timestamp > fiveMinutesAgo) {
            setExpectedJobTitle(parsedData.jobTitle);
          } else {
            // Clear outdated data
            localStorage.removeItem(`job_title_${userId}`);
          }
        }
      } catch {
        // Handle error silently
      }
    }
  }, [userId]);

  // Determine which job title to display - prioritize expected title over session
  const displayJobTitle =
    expectedJobTitle &&
    (jobTitle === "Unassigned" || expectedJobTitle !== jobTitle)
      ? expectedJobTitle
      : jobTitle;

  // Cancel any existing refresh timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }
    };
  }, []);

  // Set up Pusher client subscription to listen for job title changes
  useEffect(() => {
    if (!userId) return;

    const channel = pusherClient.subscribe("users");

    // Listen for job title updates for the current user
    channel.bind("user-job-changed", async (data: JobChangedEvent) => {
      // Only refresh if the update was for the current user
      if (data.userId === userId) {
        // Clear any existing timeouts
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
        if (spinnerTimeoutRef.current) {
          clearTimeout(spinnerTimeoutRef.current);
        }

        // Set expected job title immediately for the UI to show
        setExpectedJobTitle(data.jobTitle);
        setIsRefreshing(true);

        // Always set a timeout to stop the spinner, regardless of refresh success
        spinnerTimeoutRef.current = setTimeout(() => {
          setIsRefreshing(false);
        }, 8000); // 8 seconds maximum spinner time

        // Store expected job title in localStorage with timestamp
        try {
          localStorage.setItem(
            `job_title_${userId}`,
            JSON.stringify({
              jobTitle: data.jobTitle,
              timestamp: Date.now(),
              jobId: data.jobId,
              type: data.type,
            })
          );
        } catch {
          // Handle error silently
        }

        try {
          // For removal events, we can do fewer attempts since it's less critical
          // and just sets the job title back to default
          const maxAttempts =
            data.type === "removal" ? 3 : maxAttemptsRef.current;

          // Try multiple refresh attempts with increasing delays
          const initialDelay = 1000; // 1 second
          let successfulRefresh = false;

          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const delay = initialDelay * attempt; // 1s, 2s, 3s, etc.

            // Use a Promise with setTimeout to create a delay
            await new Promise<void>((resolve) => {
              refreshTimeoutRef.current = setTimeout(async () => {
                try {
                  await refreshSession();

                  // For removal events, just checking if the job title is now "Unassigned" or doesn't match the previous title
                  const isRemovalSuccessful =
                    data.type === "removal" &&
                    (session?.user?.jobTitle === "Unassigned" ||
                      session?.user?.jobTitle !== jobTitle);

                  // Check if refresh was successful
                  if (
                    session?.user?.jobTitle === data.jobTitle ||
                    isRemovalSuccessful
                  ) {
                    successfulRefresh = true;

                    // Stop the spinner on success
                    setIsRefreshing(false);

                    // Clear the spinner timeout since we succeeded
                    if (spinnerTimeoutRef.current) {
                      clearTimeout(spinnerTimeoutRef.current);
                      spinnerTimeoutRef.current = null;
                    }

                    resolve();
                    return; // Exit if successful
                  } else {
                    if (attempt === maxAttempts) {
                      // Keep expected job title in localStorage but stop spinner
                      if (spinnerTimeoutRef.current) {
                        clearTimeout(spinnerTimeoutRef.current);
                        setIsRefreshing(false);
                      }
                      resolve();
                    } else {
                      resolve();
                    }
                  }
                } catch {
                  resolve();
                }
              }, delay);
            });

            if (successfulRefresh) break;
          }
        } catch {
          // Make sure spinner stops in case of error
          setIsRefreshing(false);
        }
      }
    });

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }
      pusherClient.unsubscribe("users");
    };
  }, [userId, refreshSession, session]);

  // Clear expected job title when session matches it
  useEffect(() => {
    if (expectedJobTitle && jobTitle === expectedJobTitle) {
      setExpectedJobTitle(null);
      setIsRefreshing(false); // Ensure spinner stops when titles match
      // Remove from localStorage too
      if (userId) {
        localStorage.removeItem(`job_title_${userId}`);
      }
    }
  }, [jobTitle, expectedJobTitle, userId]);

  const refreshJobTitle = useCallback(async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      await refreshSession();
    } catch {
      // Handle error silently
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshSession]);

  return {
    refreshSession: refreshJobTitle,
    jobTitle: displayJobTitle,
    isRefreshing,
  };
}

interface PermissionLinkProps {
  resource: Resource;
  action: Action;
  children: React.ReactNode;
  href: string;
  className?: string;
  activeClassName?: string;
  exact?: boolean;
}

export function PermissionLink({
  resource,
  action,
  children,
  href,
  className,
  activeClassName,
  exact = true,
}: PermissionLinkProps) {
  const { checkPermission } = usePermissions();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";

  // Check if the user has permission to see this link
  if (!checkPermission(resource, action)) {
    return null;
  }

  // Determine if this link is active
  const isActive = exact
    ? pathname === href
    : href !== "/" && pathname.startsWith(href);

  const linkClassName =
    isActive && activeClassName
      ? `${className || ""} ${activeClassName}`.trim()
      : className;

  return (
    <Link href={href} className={linkClassName}>
      {children}
    </Link>
  );
}
